import * as authHelper from "../helpers/AuthHelpers";
import { clearAllCookies } from "./common/genericFunction";

export function calculateDateDifference(startDate: string, endDate: string) {
  const start: any = new Date(startDate);
  const end: any = new Date(endDate);

  const differenceInMilliseconds = end - start;

  const differenceInDays = differenceInMilliseconds / (1000 * 60 * 60 * 24);

  return differenceInDays;
}

export function planButtonLabel({ isTrue }: { isTrue: boolean }) {
  const currentPlan: string = "Current Plan";
  const upgrade: string = "Upgrade";
  return isTrue ? currentPlan : upgrade;
}

// Features enforced as an on/off gate (ceiling >= 1 means allowed) rather than a metered
// resource, matching bizro-sapiens's ApprovalHierarchyServiceImpl.enforceApprovalWorkflowCeilings:
// nothing ever decrements their count, so "0 of 1 used" misreads a feature toggle as a one-time
// quota.
const TOGGLE_STYLE_FEATURE_LABELS = /^(Approval Delegation|Parallel Approval Groups)$/i;

export function getFeatureDetails(
  value: number,
  total: number,
  label: string
): {
  percentage: string;
  label: string;
  iconName: string;
  displayText: string;
  isToggleFeature: boolean;
} {
  // Calculate percentage
  const percentage =
    total === 0 ? "0.00%" : ((value / total) * 100).toFixed(2) + "%";

  const isToggleFeature = total > 0 && TOGGLE_STYLE_FEATURE_LABELS.test(label.trim());

  // Compute display text for the usage count
  let displayText: string;
  if (total >= 999999) {
    displayText = "Unlimited";
  } else if (total === 0) {
    displayText = "Not included in your plan";
  } else if (isToggleFeature) {
    displayText = "Enabled";
  } else {
    displayText = `${value} of ${total} used`;
  }

  // Remove unwanted words from the label
  let cleanedLabel = label.replace(/Add|Create|Screen|Custom|of/g, "").trim();

  // Determine the icon name based on the label
  let iconName = "people";
  if (/job/i.test(label)) {
    iconName = "office-bag";
  } else if (/users/i.test(label)) {
    iconName = "profile-user";
  } else if (/round/i.test(label)) {
    iconName = "round";
  } else if (/Schedule/i.test(label)) {
    iconName = "schedule";
  } else if (/role/i.test(label)) {
    iconName = "user";
  } else if (/Recording/i.test(label)) {
    iconName = "abstract-5";
  } else if (/ai/i.test(label)) {
    iconName = "AI";
  } else if (/Candidates/i.test(label)) {
    iconName = "document";
    cleanedLabel = "Resume Parsing";
  }else if (/Policy/i.test(label)) {
    iconName = "document";
    cleanedLabel = "Smart Policy";
  }
  return {
    percentage,
    label: cleanedLabel,
    iconName,
    displayText,
    isToggleFeature,
  };
}

/** What is left of a feature: the plan's own allowance plus any add-ons bought. */
export function remainingCount(usage?: {
  totalAvailableCount?: number;
  addOnCount?: number;
}) {
  return (usage?.totalAvailableCount ?? 0) + (usage?.addOnCount ?? 0);
}

/**
 * Seats a product subscription has left, or null when it has no seat count
 * (then only the server can say).
 */
export function seatsLeft(subscription?: {
  companySubscriptionUsage?: any[];
}): number | null {
  const seats = subscription?.companySubscriptionUsage?.find(
    (usage: any) => usage?.features?.featuresName === "ADD_USERS"
  );
  return seats ? remainingCount(seats) : null;
}

/** Seats are counted per product: they are used up only when no product has any left. */
export function seatsUsedUpEverywhere(subscriptions: any[] = []): boolean {
  const counts = subscriptions
    .map((subscription) => seatsLeft(subscription))
    .filter((left): left is number => left !== null);
  return counts.length > 0 && counts.every((left) => left <= 0);
}

/** The first current product subscription with no seats left, to buy more for. */
export function productOutOfSeats(subscriptions: any[] = []): any {
  return subscriptions.find(
    (subscription) =>
      !subscription?.isExpired && (seatsLeft(subscription) ?? 1) <= 0
  );
}

export function addOneDay(dateString: string): string {
  const date = new Date(dateString);
  date.setDate(date.getDate() + 1);
  return date.toISOString();
}

// region Storage

export function setRegion(value: string) {
  try {
    localStorage.setItem("region", value);
  } catch (error) {
    console.error("Error setting region in localStorage:", error);
  }
}

export function getRegion() {
  try {
    return localStorage.getItem("region");
  } catch (error) {
    console.error("Error getting region from localStorage:", error);
    return null;
  }
}

export function setRegionBasedOnDomain() {
  try {
    let region = "";

    if (window.location.origin === process.env.REACT_APP_FRONTEND_URL_US) {
      region = "us";
    } else if (
      window.location.origin === process.env.REACT_APP_FRONTEND_URL_ASIA
    ) {
      region = "asia";
    } else {
      region = "asia";
    }

    localStorage.setItem("region", region);
  } catch (error) {
    console.error("Error setting region based on domain:", error);
  }
}

export function getRegionBasedBackendUrl(): string {
  try {
    const region = getRegion();
    if (region === "asia") {
      return process.env.REACT_APP_BACKEND_URL_ASIA ?? "";
    } else if (region === "us") {
      return process.env.REACT_APP_BACKEND_URL_US ?? "";
    } else {
      return process.env.REACT_APP_BACKEND_URL_ASIA ?? "";
    }
  } catch (error) {
    console.error("Error getting region-based URL:", error);
    return process.env.REACT_APP_BACKEND_URL_ASIA ?? "";
  }
}

export function localLogout() {
  authHelper.removeAuth();
  clearAllCookies();
  localStorage.clear();
}

export function getRegionWiseRedirectUrl(region: string) {
  if (
    region === "us" &&
    window.location.origin !== process.env.REACT_APP_FRONTEND_URL_US
  ) {
    localLogout();
    window.location.href = process.env.REACT_APP_FRONTEND_URL_US ?? "";
  } else if (
    region === "asia" &&
    window.location.origin !== process.env.REACT_APP_FRONTEND_URL_ASIA
  ) {
    localLogout();
    window.location.href = process.env.REACT_APP_FRONTEND_URL_ASIA ?? "";
  }
}

const regionLocale = (currencyCode: string) => {
  if (currencyCode === "USD") {
    return "en-US";
  } else if (currencyCode === "INR") {
    return "en-IN";
  }
};

export function formatCurrency(amount: any, currencyCode = "USD") {
  return new Intl.NumberFormat(regionLocale(currencyCode), {
    style: "currency",
    currency: currencyCode,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
}
export function isEffectivePlan(item?: {
  effectiveFrom?: string;
  effectiveTo?: string;
}): boolean {
  const from = new Date(item?.effectiveFrom ?? "");
  const to = new Date(item?.effectiveTo ?? "");

  if (isNaN(from.getTime()) || isNaN(to.getTime())) {
    return false;
  }

  const today = new Date();
  return today >= from && today <= to;
}
